import { Head, router } from "@inertiajs/react"
import { useState, useCallback } from "react"
import AdminLayout from "@/components/admin/layout"
import Pagination from "@/components/pagination"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface AdminListing {
  id: number
  slug: string
  address: string
  status: string
  listing_type: string
  price: string | null
  image_url: string | null
  image_count: number
  owner_name: string
  account_name: string
  total_cost_cents: number
  created_at: string
}

interface PaginationMeta {
  page: number
  limit: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  search: string | null
  status: string | null
  listing_type: string | null
  sort: string
  direction: string
  listings: AdminListing[]
  pagination: PaginationMeta
  counts: {
    all: number
    draft: number
    active: number
    archived: number
  }
}

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
] as const

const TYPE_OPTIONS = [
  { value: "all", label: "All types" },
  { value: "sale", label: "For sale" },
  { value: "long_let", label: "Long let" },
  { value: "short_let", label: "Short let" },
  { value: "holiday_let", label: "Holiday let" },
] as const

const STATUS_COLOURS: Record<string, string> = {
  draft: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  archived: "bg-cream/10 text-cream/60 border-cream/20",
}

const LISTING_TYPE_LABELS: Record<string, string> = {
  sale: "For sale",
  long_let: "Long let",
  short_let: "Short let",
  holiday_let: "Holiday let",
}

const COLUMNS = [
  { key: "image", label: "", sortable: false },
  { key: "address", label: "Address", sortable: true },
  { key: "status", label: "Status", sortable: false },
  { key: "listing_type", label: "Type", sortable: false },
  { key: "owner", label: "Owner", sortable: false },
  { key: "account", label: "Account", sortable: false },
  { key: "total_cost_cents", label: "Cost", sortable: true },
  { key: "created_at", label: "Created", sortable: true },
] as const

export default function AdminListingsIndex({
  search,
  status,
  listing_type,
  sort,
  direction,
  listings,
  pagination,
  counts,
}: Props) {
  const [searchValue, setSearchValue] = useState(search || "")

  const buildParams = useCallback(
    (overrides: Record<string, string | undefined> = {}) => {
      const p: Record<string, string> = {}
      if (search) p.search = search
      if (status) p.status = status
      if (listing_type) p.listing_type = listing_type
      if (sort !== "created_at") p.sort = sort
      if (direction !== "desc") p.direction = direction
      for (const [k, v] of Object.entries(overrides)) {
        if (v === undefined || v === "") {
          delete p[k]
        } else {
          p[k] = v
        }
      }
      return p
    },
    [search, status, listing_type, sort, direction],
  )

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get(
      "/admin/listings",
      buildParams({ search: searchValue || undefined, page: undefined }),
      { preserveState: true },
    )
  }

  function handleSort(column: string) {
    const newDirection = sort === column && direction === "asc" ? "desc" : "asc"
    router.get(
      "/admin/listings",
      buildParams({ sort: column, direction: newDirection, page: undefined }),
      { preserveState: true },
    )
  }

  function handleFilter(key: string, value: string) {
    const val = value === "all" ? undefined : value
    router.get("/admin/listings", buildParams({ [key]: val, page: undefined }), {
      preserveState: true,
    })
  }

  return (
    <AdminLayout>
      <Head title="Listings | Admin" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-display font-semibold text-cream">Listings</h1>
          <p className="mt-1 text-sm text-cream/50">
            {pagination.count} {pagination.count === 1 ? "listing" : "listings"} across all
            accounts
          </p>
        </div>

        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2">
            <Select
              value={status || "all"}
              onValueChange={(v) => handleFilter("status", v)}
            >
              <SelectTrigger className="w-[140px] border-white/10 bg-white/5 text-cream">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="dark border-white/10 bg-charcoal">
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                    {opt.value !== "all" && (
                      <span className="ml-1 text-[10px] opacity-50">
                        {counts[opt.value as keyof typeof counts]}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={listing_type || "all"}
              onValueChange={(v) => handleFilter("listing_type", v)}
            >
              <SelectTrigger className="w-[140px] border-white/10 bg-white/5 text-cream">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="dark border-white/10 bg-charcoal">
                {TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative">
              <SearchIcon className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-cream/30" />
              <Input
                placeholder="Search address..."
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="h-8 w-56 border-white/10 bg-white/5 pl-8 text-sm text-cream placeholder:text-cream/30"
              />
            </div>
            {search && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-cream/50 hover:text-cream"
                onClick={() => {
                  setSearchValue("")
                  router.get(
                    "/admin/listings",
                    buildParams({ search: undefined, page: undefined }),
                    { preserveState: true },
                  )
                }}
              >
                Clear
              </Button>
            )}
          </form>
        </div>

        <div className="overflow-x-auto rounded-lg border border-white/[0.06]">
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="border-b border-white/[0.06] bg-white/[0.02] text-left">
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    className="px-4 py-2.5 text-xs font-medium uppercase tracking-wider text-cream/40"
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 transition-colors hover:text-cream/70"
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                        <SortIcon
                          active={sort === col.key}
                          direction={sort === col.key ? direction : undefined}
                        />
                      </button>
                    ) : (
                      col.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {listings.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-cream/30">
                    {search ? `No listings matching "${search}"` : "No listings found"}
                  </td>
                </tr>
              )}
              {listings.map((listing) => (
                <tr
                  key={listing.id}
                  className="cursor-pointer border-b border-white/[0.04] text-cream/70 transition-colors hover:bg-white/[0.04]"
                  onClick={() => router.visit(`/admin/listings/${listing.id}`)}
                >
                  <td className="px-4 py-2.5">
                    {listing.image_url ? (
                      <img
                        src={listing.image_url}
                        alt=""
                        className="size-10 shrink-0 rounded object-cover"
                      />
                    ) : (
                      <div className="flex size-10 shrink-0 items-center justify-center rounded bg-white/5 text-cream/20">
                        <ImagePlaceholderIcon className="size-5" />
                      </div>
                    )}
                  </td>
                  <td className="max-w-[180px] truncate px-4 py-2.5 font-medium text-cream">
                    {listing.address}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5">
                    <Badge className={STATUS_COLOURS[listing.status] || STATUS_COLOURS.draft}>
                      {listing.status}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-cream/50">
                    {LISTING_TYPE_LABELS[listing.listing_type] || listing.listing_type}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-cream/50">
                    {listing.owner_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-cream/50">
                    {listing.account_name}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-xs tabular-nums text-cream/50">
                    {formatCurrency(listing.total_cost_cents)}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-xs text-cream/50">
                    {formatDate(listing.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination baseUrl="/admin/listings" params={buildParams()} pagination={pagination} />
      </div>
    </AdminLayout>
  )
}

function SortIcon({ active, direction }: { active: boolean; direction?: string }) {
  if (!active) {
    return (
      <svg
        className="size-3 opacity-30"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8.25 15 12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
        />
      </svg>
    )
  }
  return direction === "asc" ? (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
    </svg>
  ) : (
    <svg className="size-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  )
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z"
      />
    </svg>
  )
}

function ImagePlaceholderIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z"
      />
    </svg>
  )
}

function formatCurrency(cents: number): string {
  const dollars = cents / 100
  if (dollars >= 1) return `$${dollars.toFixed(2)}`
  return `${cents}c`
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  })
}
